import { useEffect, useRef, useState } from 'react';

/**
 * Pupil — bolinha preta dentro de uma "cara" que segue o cursor (clamped to maxDistance).
 * Pode ser forçada a olhar para uma direcção via forceLookX/Y (ex: olhar para a senha).
 */
function Pupil({
  size = 12,
  maxDistance = 5,
  pupilColor = 'black',
  forceLookX,
  forceLookY,
}: {
  size?: number;
  maxDistance?: number;
  pupilColor?: string;
  forceLookX?: number;
  forceLookY?: number;
}) {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
    };
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, []);

  const calc = () => {
    if (forceLookX !== undefined && forceLookY !== undefined) {
      return { x: forceLookX, y: forceLookY };
    }
    if (!ref.current) return { x: 0, y: 0 };
    const p = ref.current.getBoundingClientRect();
    const cx = p.left + p.width / 2;
    const cy = p.top + p.height / 2;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
    const a = Math.atan2(dy, dx);
    return { x: Math.cos(a) * dist, y: Math.sin(a) * dist };
  };

  const pp = calc();
  return (
    <div
      ref={ref}
      className="rounded-full"
      style={{
        width: size,
        height: size,
        backgroundColor: pupilColor,
        transform: `translate(${pp.x}px, ${pp.y}px)`,
        transition: 'transform 0.1s ease-out',
      }}
    />
  );
}

/**
 * EyeBall — olho completo (branco + pupila) com animação de pestanejar.
 */
function EyeBall({
  size = 48,
  pupilSize = 16,
  maxDistance = 10,
  eyeColor = 'white',
  pupilColor = 'black',
  isBlinking = false,
  forceLookX,
  forceLookY,
}: {
  size?: number;
  pupilSize?: number;
  maxDistance?: number;
  eyeColor?: string;
  pupilColor?: string;
  isBlinking?: boolean;
  forceLookX?: number;
  forceLookY?: number;
}) {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
    };
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, []);

  const calc = () => {
    if (forceLookX !== undefined && forceLookY !== undefined) {
      return { x: forceLookX, y: forceLookY };
    }
    if (!ref.current) return { x: 0, y: 0 };
    const e = ref.current.getBoundingClientRect();
    const cx = e.left + e.width / 2;
    const cy = e.top + e.height / 2;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    const dist = Math.min(Math.sqrt(dx * dx + dy * dy), maxDistance);
    const a = Math.atan2(dy, dx);
    return { x: Math.cos(a) * dist, y: Math.sin(a) * dist };
  };

  const pp = calc();
  return (
    <div
      ref={ref}
      className="rounded-full flex items-center justify-center transition-all duration-150"
      style={{
        width: size,
        height: isBlinking ? 2 : size,
        backgroundColor: eyeColor,
        overflow: 'hidden',
      }}
    >
      {!isBlinking && (
        <div
          className="rounded-full"
          style={{
            width: pupilSize,
            height: pupilSize,
            backgroundColor: pupilColor,
            transform: `translate(${pp.x}px, ${pp.y}px)`,
            transition: 'transform 0.1s ease-out',
          }}
        />
      )}
    </div>
  );
}

/**
 * AuthCharacters — coluna decorativa da página de auth com 4 "personagens"
 * abstractos que seguem o cursor, pestanejam, e reagem ao estado do form
 * (a olhar uns para os outros quando o user escreve; a espreitar quando a
 * password fica visível). Visual monocromático (cinzas + ink).
 *
 * Props para o pai informar o estado do form:
 *   - isTyping: utilizador está activamente a escrever
 *   - pwShow: password está visível (eye toggle ON)
 *   - pwHide: password está escondida mas tem caracteres digitados
 */
export function AuthCharacters({
  isTyping = false,
  pwShow = false,
  pwHide = false,
}: {
  isTyping?: boolean;
  pwShow?: boolean;
  pwHide?: boolean;
}) {
  const [mouseX, setMouseX] = useState(0);
  const [mouseY, setMouseY] = useState(0);
  const [isPurpleBlinking, setIsPurpleBlinking] = useState(false);
  const [isBlackBlinking, setIsBlackBlinking] = useState(false);
  const [isLookingAtEachOther, setIsLookingAtEachOther] = useState(false);
  const [isPurplePeeking, setIsPurplePeeking] = useState(false);

  const purpleRef = useRef<HTMLDivElement>(null);
  const blackRef = useRef<HTMLDivElement>(null);
  const yellowRef = useRef<HTMLDivElement>(null);
  const orangeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const h = (e: MouseEvent) => {
      setMouseX(e.clientX);
      setMouseY(e.clientY);
    };
    window.addEventListener('mousemove', h);
    return () => window.removeEventListener('mousemove', h);
  }, []);

  // Random blinks for the two tall characters (purple/black)
  useEffect(() => {
    const rnd = () => Math.random() * 4000 + 3000;
    let t: ReturnType<typeof setTimeout>;
    const sched = () => {
      t = setTimeout(() => {
        setIsPurpleBlinking(true);
        setTimeout(() => {
          setIsPurpleBlinking(false);
          sched();
        }, 150);
      }, rnd());
    };
    sched();
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const rnd = () => Math.random() * 4000 + 3000;
    let t: ReturnType<typeof setTimeout>;
    const sched = () => {
      t = setTimeout(() => {
        setIsBlackBlinking(true);
        setTimeout(() => {
          setIsBlackBlinking(false);
          sched();
        }, 150);
      }, rnd());
    };
    sched();
    return () => clearTimeout(t);
  }, []);

  // "Looking at each other" when user types in any field
  useEffect(() => {
    if (isTyping) {
      setIsLookingAtEachOther(true);
      const t = setTimeout(() => setIsLookingAtEachOther(false), 800);
      return () => clearTimeout(t);
    }
    setIsLookingAtEachOther(false);
  }, [isTyping]);

  // Purple character "peeks" at the password when it's visible
  useEffect(() => {
    if (pwShow) {
      const t = setTimeout(() => {
        setIsPurplePeeking(true);
        setTimeout(() => setIsPurplePeeking(false), 800);
      }, Math.random() * 3000 + 2000);
      return () => clearTimeout(t);
    }
    setIsPurplePeeking(false);
  }, [pwShow, isPurplePeeking]);

  const calcPos = (ref: React.RefObject<HTMLDivElement>) => {
    if (!ref.current) return { faceX: 0, faceY: 0, bodySkew: 0 };
    const r = ref.current.getBoundingClientRect();
    const cx = r.left + r.width / 2;
    const cy = r.top + r.height / 3;
    const dx = mouseX - cx;
    const dy = mouseY - cy;
    return {
      faceX: Math.max(-15, Math.min(15, dx / 20)),
      faceY: Math.max(-10, Math.min(10, dy / 30)),
      bodySkew: Math.max(-6, Math.min(6, -dx / 120)),
    };
  };

  const purplePos = calcPos(purpleRef);
  const blackPos = calcPos(blackRef);
  const yellowPos = calcPos(yellowRef);
  const orangePos = calcPos(orangeRef);

  return (
    <div className="relative" style={{ width: 550, height: 400 }}>
      {/* Personagens em ramp de cinzas (ink → cinza médio) — todos visíveis
          em fundo branco. Eram #1a1a1a/#555/#D0D0D0/#F5F5F5 quando o fundo
          era escuro; agora ajustados para terem contraste em fundo claro. */}

      {/* Tall dark character */}
      <div
        ref={purpleRef}
        className="absolute bottom-0 transition-all duration-700 ease-in-out"
        style={{
          left: 70,
          width: 180,
          height: isTyping || pwHide ? 440 : 400,
          backgroundColor: '#1a1a1a',
          borderRadius: '10px 10px 0 0',
          zIndex: 1,
          transform: pwShow
            ? 'skewX(0deg)'
            : isTyping || pwHide
              ? `skewX(${(purplePos.bodySkew || 0) - 12}deg) translateX(40px)`
              : `skewX(${purplePos.bodySkew || 0}deg)`,
          transformOrigin: 'bottom center',
        }}
      >
        <div
          className="absolute flex gap-8 transition-all duration-700 ease-in-out"
          style={{
            left: pwShow ? 20 : isLookingAtEachOther ? 55 : 45 + purplePos.faceX,
            top: pwShow ? 35 : isLookingAtEachOther ? 65 : 40 + purplePos.faceY,
          }}
        >
          <EyeBall
            size={18}
            pupilSize={7}
            maxDistance={5}
            eyeColor="white"
            pupilColor="#2D2D2D"
            isBlinking={isPurpleBlinking}
            forceLookX={pwShow ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
            forceLookY={pwShow ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
          />
          <EyeBall
            size={18}
            pupilSize={7}
            maxDistance={5}
            eyeColor="white"
            pupilColor="#2D2D2D"
            isBlinking={isPurpleBlinking}
            forceLookX={pwShow ? (isPurplePeeking ? 4 : -4) : isLookingAtEachOther ? 3 : undefined}
            forceLookY={pwShow ? (isPurplePeeking ? 5 : -4) : isLookingAtEachOther ? 4 : undefined}
          />
        </div>
      </div>

      {/* Mid "black" character (#555555) */}
      <div
        ref={blackRef}
        className="absolute bottom-0 transition-all duration-700 ease-in-out"
        style={{
          left: 240,
          width: 120,
          height: 310,
          backgroundColor: '#555555',
          borderRadius: '8px 8px 0 0',
          zIndex: 2,
          transform: pwShow
            ? 'skewX(0deg)'
            : isLookingAtEachOther
              ? `skewX(${(blackPos.bodySkew || 0) * 1.5 + 10}deg) translateX(20px)`
              : isTyping || pwHide
                ? `skewX(${(blackPos.bodySkew || 0) * 1.5}deg)`
                : `skewX(${blackPos.bodySkew || 0}deg)`,
          transformOrigin: 'bottom center',
        }}
      >
        <div
          className="absolute flex gap-6 transition-all duration-700 ease-in-out"
          style={{
            left: pwShow ? 10 : isLookingAtEachOther ? 32 : 26 + blackPos.faceX,
            top: pwShow ? 28 : isLookingAtEachOther ? 12 : 32 + blackPos.faceY,
          }}
        >
          <EyeBall
            size={16}
            pupilSize={6}
            maxDistance={4}
            eyeColor="white"
            pupilColor="#2D2D2D"
            isBlinking={isBlackBlinking}
            forceLookX={pwShow ? -4 : isLookingAtEachOther ? 0 : undefined}
            forceLookY={pwShow ? -4 : isLookingAtEachOther ? -4 : undefined}
          />
          <EyeBall
            size={16}
            pupilSize={6}
            maxDistance={4}
            eyeColor="white"
            pupilColor="#2D2D2D"
            isBlinking={isBlackBlinking}
            forceLookX={pwShow ? -4 : isLookingAtEachOther ? 0 : undefined}
            forceLookY={pwShow ? -4 : isLookingAtEachOther ? -4 : undefined}
          />
        </div>
      </div>

      {/* Round "orange" character (light grey dome) */}
      <div
        ref={orangeRef}
        className="absolute bottom-0 transition-all duration-700 ease-in-out"
        style={{
          left: 0,
          width: 240,
          height: 200,
          zIndex: 3,
          backgroundColor: '#8a8a8a',
          borderRadius: '120px 120px 0 0',
          transform: pwShow
            ? 'skewX(0deg)'
            : `skewX(${orangePos.bodySkew || 0}deg)`,
          transformOrigin: 'bottom center',
        }}
      >
        <div
          className="absolute flex gap-8 transition-all duration-200 ease-out"
          style={{
            left: pwShow ? 50 : 82 + (orangePos.faceX || 0),
            top: pwShow ? 85 : 90 + (orangePos.faceY || 0),
          }}
        >
          <Pupil
            size={12}
            maxDistance={5}
            pupilColor="#2D2D2D"
            forceLookX={pwShow ? -5 : undefined}
            forceLookY={pwShow ? -4 : undefined}
          />
          <Pupil
            size={12}
            maxDistance={5}
            pupilColor="#2D2D2D"
            forceLookX={pwShow ? -5 : undefined}
            forceLookY={pwShow ? -4 : undefined}
          />
        </div>
      </div>

      {/* Round "yellow" character (lightest grey dome) */}
      <div
        ref={yellowRef}
        className="absolute bottom-0 transition-all duration-700 ease-in-out"
        style={{
          left: 310,
          width: 140,
          height: 230,
          backgroundColor: '#b8b8b8',
          borderRadius: '70px 70px 0 0',
          zIndex: 4,
          transform: pwShow
            ? 'skewX(0deg)'
            : `skewX(${yellowPos.bodySkew || 0}deg)`,
          transformOrigin: 'bottom center',
        }}
      >
        <div
          className="absolute flex gap-6 transition-all duration-200 ease-out"
          style={{
            left: pwShow ? 20 : 52 + (yellowPos.faceX || 0),
            top: pwShow ? 35 : 40 + (yellowPos.faceY || 0),
          }}
        >
          <Pupil
            size={12}
            maxDistance={5}
            pupilColor="#2D2D2D"
            forceLookX={pwShow ? -5 : undefined}
            forceLookY={pwShow ? -4 : undefined}
          />
          <Pupil
            size={12}
            maxDistance={5}
            pupilColor="#2D2D2D"
            forceLookX={pwShow ? -5 : undefined}
            forceLookY={pwShow ? -4 : undefined}
          />
        </div>
        <div
          className="absolute rounded-full transition-all duration-200 ease-out"
          style={{
            width: 80,
            height: 4,
            backgroundColor: '#555555',
            left: pwShow ? 10 : 40 + (yellowPos.faceX || 0),
            top: pwShow ? 88 : 88 + (yellowPos.faceY || 0),
          }}
        />
      </div>
    </div>
  );
}
